import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Message
} from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_API_URL;

interface MotivationalMessage {
  id: string;
  message_english: string;
  message_telugu: string;
  audience: string;
  status: boolean;
  deleted: boolean;
  created_by: string;
  created_date: string;
  updated_date: string;
}

export default function MotivationalMessagesPage() {
  const { user } = useAuth();
  const [messagesList, setMessagesList] = useState<MotivationalMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<MotivationalMessage | null>(null);
  const [formData, setFormData] = useState({
    message_english: '',
    message_telugu: '',
    audience: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchMessagesList = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      if (!token) {
        setError('Authentication token not found');
        return;
      }

      const response = await fetch(`${API_URL}/motivational-messages/`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'accept': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch motivational messages');
      }

      const data = await response.json();
      setMessagesList(data);
    } catch (error) {
      console.error('Error fetching motivational messages:', error);
      setError(error instanceof Error ? error.message : 'Failed to fetch motivational messages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role === 'superadmin') {
      fetchMessagesList();
    }
  }, [user]);

  const handleAddMessage = () => {
    setSelectedMessage(null);
    setFormData({
      message_english: '',
      message_telugu: '',
      audience: ''
    });
    setOpenDialog(true);
  };

  const handleEditMessage = (message: MotivationalMessage) => {
    setSelectedMessage(message);
    setFormData({
      message_english: message.message_english,
      message_telugu: message.message_telugu,
      audience: message.audience
    });
    setOpenDialog(true);
  };

  const handleSaveMessage = async () => {
    setError('');
    setSuccess('');

    // Validation
    if (!formData.message_english.trim()) {
      setError('English message is required');
      return;
    }

    if (!formData.message_telugu.trim()) {
      setError('Telugu message is required');
      return;
    }

    if (!formData.audience) {
      setError('Audience is required');
      return;
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');

      if (!token) {
        setError('Authentication token not found');
        return;
      }

      const url = selectedMessage
        ? `${API_URL}/motivational-messages/${selectedMessage.id}`
        : `${API_URL}/motivational-messages/`;

      const method = selectedMessage ? 'PUT' : 'POST';

      const payload = {
        message_english: formData.message_english.trim(),
        message_telugu: formData.message_telugu.trim(),
        audience: formData.audience
      };

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setSuccess(`Motivational message ${selectedMessage ? 'updated' : 'created'} successfully!`);
        setSubmitting(false);
        setTimeout(async () => {
          setOpenDialog(false);
          await fetchMessagesList();
        }, 1500);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || `Failed to ${selectedMessage ? 'update' : 'create'} motivational message`);
        setSubmitting(false);
      }
    } catch (err) {
      console.error('Error saving motivational message:', err);
      setError(`An error occurred while ${selectedMessage ? 'updating' : 'creating'} the motivational message`);
      setSubmitting(false);
    }
  };

  const handleDeleteMessage = async (messageId: string) => {
    if (!window.confirm('Are you sure you want to delete this motivational message?')) return;

    try {
      setLoading(true);
      setError('');
      setSuccess('');
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication token not found');
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_URL}/motivational-messages/${messageId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'accept': 'application/json'
        }
      });

      if (response.ok) {
        setSuccess('Motivational message deleted successfully!');
        await fetchMessagesList();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to delete motivational message');
      }
    } catch (err) {
      setError('An error occurred while deleting the motivational message');
    } finally {
      setLoading(false);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'message_english',
      headerName: 'Message (English)',
      width: 300,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <Typography variant="body2" noWrap>
            {params.value}
          </Typography>
        </Tooltip>
      )
    },
    {
      field: 'message_telugu',
      headerName: 'Message (Telugu)',
      width: 300,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <Typography variant="body2" noWrap>
            {params.value}
          </Typography>
        </Tooltip>
      )
    },
    {
      field: 'audience',
      headerName: 'Audience',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
          {params.value}
        </Typography>
      )
    },
    {
      field: 'created_date',
      headerName: 'Created At',
      width: 200,
      valueFormatter: (value: any) => {
        const date = new Date(value);
        return date.toLocaleString('en-IN', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        });
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Edit Message">
            <IconButton size="small" color="primary" onClick={() => handleEditMessage(params.row)}>
              <Edit />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Message">
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDeleteMessage(params.row.id)}
            >
              <Delete />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  if (user?.role !== 'superadmin') {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          You do not have permission to access this page.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Global success/error messages */}
      {error && !openDialog && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && !openDialog && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Motivational Messages
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddMessage}
        >
          Add New Message
        </Button>
      </Box>

      {/* Messages Table */}
      <Card>
        <CardContent>
          <DataGrid
            rows={messagesList || []}
            columns={columns}
            pageSizeOptions={[10, 25, 50]}
            disableRowSelectionOnClick
            autoHeight
            loading={loading}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 10 }
              }
            }}
            sx={{
              '& .MuiDataGrid-row:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          />
        </CardContent>
      </Card>

      {/* Add/Edit Message Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedMessage ? 'Edit Motivational Message' : 'Add New Motivational Message'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2, mt: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2, mt: 2 }}>
              {success}
            </Alert>
          )}

          <Box sx={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: 2,
            mt: 1
          }}>
            <TextField
              fullWidth
              label="Message (English)"
              value={formData.message_english}
              onChange={(e) => setFormData({ ...formData, message_english: e.target.value })}
              required
              multiline
              rows={3}
              helperText="Enter the motivational message in English"
              placeholder="e.g., Stay healthy, stay happy!"
            />
            <TextField
              fullWidth
              label="Message (Telugu)"
              value={formData.message_telugu}
              onChange={(e) => setFormData({ ...formData, message_telugu: e.target.value })}
              required
              multiline
              rows={3}
              helperText="Enter the motivational message in Telugu"
              placeholder="e.g., ఆరోగ్యంగా ఉండండి, సంతోషంగా ఉండండి!"
            />
            <FormControl fullWidth required>
              <InputLabel>Audience</InputLabel>
              <Select
                value={formData.audience}
                onChange={(e) => setFormData({ ...formData, audience: e.target.value })}
                label="Audience"
              >
                <MenuItem value="patient">Patient</MenuItem>
                <MenuItem value="family">Family</MenuItem>
                <MenuItem value="doctor">Doctor</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSaveMessage} variant="contained" disabled={submitting}>
            {submitting ? <CircularProgress size={24} /> : selectedMessage ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
